import React from "react";

/*
 * File-type icons — Untitled-UI style: solid colored sheet with rounded
 * corners, a folded top-right corner (white @ 30%), and the uppercase
 * extension in bold white near the bottom. Pure SVG, theme-proof.
 */

/** A family's colour and human name, plus the tag drawn on the sheet. */
interface FileTypeMeta {
  color: string;
  label: string;
  tag: string;
}

/* Families define color; extensions fan out from them. */
const FAM: Record<string, Omit<FileTypeMeta, "tag">> = {
  pdf:    { color: "#D92D20", label: "PDF" },
  doc:    { color: "#155EEF", label: "Document" },
  text:   { color: "#344054", label: "Text" },
  sheet:  { color: "#079455", label: "Spreadsheet" },
  slides: { color: "#DC6803", label: "Slides" },
  code:   { color: "#444CE7", label: "Code" },
  data:   { color: "#B54708", label: "Data" },
  web:    { color: "#0086C9", label: "HTML" },
  link:   { color: "#475467", label: "Link" },
  audio:  { color: "#E31B54", label: "Audio" },
  video:  { color: "#6938EF", label: "Video" },
  zip:    { color: "#667085", label: "Archive" },
  font:   { color: "#3E4784", label: "Font" },
  design: { color: "#9E77ED", label: "Design" },
  book:   { color: "#0E9384", label: "eBook" },
  db:     { color: "#363F72", label: "Database" },
  image:  { color: "#7F56D9", label: "Image" },
  file:   { color: "#98A2B3", label: "File" },
};

const EXTS: Record<string, string[]> = {
  pdf:    ["pdf"],
  doc:    ["doc", "docx", "rtf", "odt", "pages"],
  text:   ["txt", "md", "note", "tex", "log"],
  sheet:  ["csv", "tsv", "xls", "xlsx", "ods", "numbers", "sheet"],
  slides: ["ppt", "pptx", "key", "odp", "deck", "slides"],
  code:   ["code", "js", "jsx", "ts", "tsx", "py", "rb", "go", "rs", "java", "kt",
           "swift", "c", "h", "cpp", "cs", "php", "sh", "css", "scss", "sql", "r", "lua", "vue"],
  data:   ["json", "yaml", "yml", "toml", "xml", "ini", "env"],
  web:    ["html", "htm"],
  link:   ["link", "url"],
  audio:  ["audio", "mp3", "wav", "m4a", "flac", "ogg", "aac"],
  video:  ["video", "mp4", "mov", "avi", "mkv", "webm", "m4v"],
  zip:    ["zip", "rar", "7z", "tar", "gz", "tgz", "dmg"],
  font:   ["font", "ttf", "otf", "woff", "woff2"],
  design: ["fig", "psd", "ai", "sketch", "xd"],
  book:   ["epub", "mobi", "azw"],
  db:     ["db", "sqlite", "mdb", "parquet"],
  image:  ["image", "png", "jpg", "jpeg", "gif", "webp", "svg", "heic", "avif", "bmp", "tiff", "ico"],
  file:   ["file"],
};

/* Label text where uppercased extension is wrong or too long */
const TAG_OVERRIDE: Record<string, string> = {
  sheet: "XLS", deck: "PPT", slides: "PPT", code: "CODE", audio: "AUD", video: "VID",
  font: "FONT", link: "URL", url: "URL", file: "FILE", note: "TXT", pages: "PGS",
  numbers: "NUM", sketch: "SKT", parquet: "PQT", sqlite: "DB", mdb: "DB", image: "IMG",
};

/* Design tools keep their brand colors */
const COLOR_OVERRIDE: Record<string, string> = { fig: "#A259FF", psd: "#31A8FF", ai: "#FF9A00", sketch: "#FDB300", xd: "#FF61F6" };

const FILE_TYPES: Record<string, FileTypeMeta> = {};
for (const [fam, exts] of Object.entries(EXTS)) {
  for (const e of exts) {
    FILE_TYPES[e] = {
      ...FAM[fam],
      color: COLOR_OVERRIDE[e] || FAM[fam].color,
      tag: (TAG_OVERRIDE[e] || e.toUpperCase()).slice(0, 5),
    };
  }
}

export const fileType = (kind: string | null | undefined): FileTypeMeta => {
  const k = String(kind || "").toLowerCase();
  if (FILE_TYPES[k]) return FILE_TYPES[k];
  // unknown but plausible extension → generic sheet with its own tag
  if (/^[a-z0-9]{1,5}$/.test(k)) return { ...FILE_TYPES.file, tag: k.toUpperCase() };
  return FILE_TYPES.file;
};

export function FileTypeIcon({ kind, size = 32, className = "" }: {
  kind?: string | null;
  size?: number;
  className?: string;
}) {
  const t = fileType(kind);
  // Ceiling: <text> instead of hand-traced glyph paths — same look, works for any extension
  const fontSize = t.tag.length >= 5 ? 7.5 : t.tag.length === 4 ? 8.5 : 10;
  return (
    <span className={`shrink-0 flex items-center justify-center ${className}`} style={{ width: size, height: size }}>
      <svg viewBox="0 0 40 40" style={{ width: size, height: size }} fill="none" aria-hidden="true">
        <path fill={t.color} d="M4 4a4 4 0 0 1 4-4h16l12 12v24a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4z" />
        <path fill="#fff" d="m24 0 12 12h-8a4 4 0 0 1-4-4z" opacity=".3" />
        <text
          x="20"
          y="28.5"
          textAnchor="middle"
          dominantBaseline="central"
          fill="#fff"
          style={{ font: `800 ${fontSize}px ui-sans-serif, system-ui, sans-serif`, letterSpacing: ".2px" }}
        >
          {t.tag}
        </text>
      </svg>
    </span>
  );
}
